import type { BibleBookId, BibleRegistryEntry, RegistryChapterStatus } from "./bibleRegistry.types";
import { PROTESTANT_BOOK_IDS, PROTESTANT_BOOKS } from "./books";
import { BIBLE_REGISTRY_ENTRIES } from "./registry/entries.generated";

export const BIBLE_REGISTRY: readonly BibleRegistryEntry[] = BIBLE_REGISTRY_ENTRIES;

export function getRegistryEntryById(id: string): BibleRegistryEntry | undefined {
  return BIBLE_REGISTRY.find((entry) => entry.id === id);
}

export function getRegistryEntriesByBook(book: BibleBookId): BibleRegistryEntry[] {
  return BIBLE_REGISTRY.filter((entry) => entry.book === book);
}

export function getRegistryBooksRepresented(): BibleBookId[] {
  return [...new Set(BIBLE_REGISTRY.map((entry) => entry.book))];
}

export function listMissingProtestantBooks(): BibleBookId[] {
  const present = new Set(getRegistryBooksRepresented());
  return PROTESTANT_BOOK_IDS.filter((id) => !present.has(id));
}

/** Asserts the registry covers every Protestant book and has unique ids. */
export function validateBibleRegistry(): {
  ok: boolean;
  entryCount: number;
  bookCount: number;
  missingBooks: BibleBookId[];
  duplicateIds: string[];
} {
  const ids = BIBLE_REGISTRY.map((entry) => entry.id);
  const duplicateIds = ids.filter((id, index) => ids.indexOf(id) !== index);
  const missingBooks = listMissingProtestantBooks();
  return {
    ok: missingBooks.length === 0 && duplicateIds.length === 0 && BIBLE_REGISTRY.length > 0,
    entryCount: BIBLE_REGISTRY.length,
    bookCount: getRegistryBooksRepresented().length,
    missingBooks,
    duplicateIds: [...new Set(duplicateIds)],
  };
}

export function getPlayableRegistryEntries(): BibleRegistryEntry[] {
  return BIBLE_REGISTRY.filter((entry) => entry.status === "available");
}

export function getComingSoonRegistryEntries(): BibleRegistryEntry[] {
  return BIBLE_REGISTRY.filter(
    (entry) => entry.status === "comingSoon" || entry.status === "locked",
  );
}

export function groupRegistryByBook(): {
  book: (typeof PROTESTANT_BOOKS)[number];
  entries: BibleRegistryEntry[];
}[] {
  return PROTESTANT_BOOKS.map((book) => ({
    book,
    entries: getRegistryEntriesByBook(book.id),
  })).filter((group) => group.entries.length > 0);
}

export type { BibleBookId, BibleRegistryEntry, RegistryChapterStatus };
